/**
 * PR-E2 — `THREE.ShaderMaterial` factory for SDF-rendered text.
 *
 * Given a loaded `SdfAtlas` (from `sdfAtlasLoader.ts`) and an optional
 * fallback color, this returns a `THREE.ShaderMaterial` wired to the
 * vertex + fragment shaders in `textShaders.glsl.ts`.
 *
 * # Material parameters
 *
 *   - `transparent: true`     — text alpha needs blending.
 *   - `depthWrite: false`     — don't write to the depth buffer (would
 *     create halos around glyph quads where the un-rendered alpha
 *     would still fill the depth buffer and occlude entities behind).
 *   - `depthTest: true`       — but DO test against existing depth so
 *     hatch / wipeout / line draw order stays correct.
 *   - `side: DoubleSide`      — text is flat; if the camera ever flips
 *     (PR-L 3D mode) we still want it visible from behind.
 *   - `blending: NormalBlending` — standard srcAlpha/oneMinusSrcAlpha.
 *
 * # Uniforms
 *
 *   - `atlas`         (sampler2D) — bound to `atlas.texture`.
 *   - `distanceRange` (float)     — `atlas.distanceRange`.
 *   - `atlasSize`     (float)     — `atlas.atlasSize`.
 *
 * # Selection state texture
 *
 * PR-E2 does NOT wire the per-instance selection-state texture from
 * `selectionStateTexture.ts` into the text material. The shader-state
 * extension lives in `stateAwareMaterial.ts` and lands together with
 * PR-G grip integration; until then text entities are not selectable.
 * The accent color parameter exists for symmetry with the line
 * pipeline (and so future PRs can flip a uniform at draw time without
 * a full material rebuild) but is not consumed by the PR-E2 shader.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1
 *
 * No simplifications: every atlas-derived parameter (distanceRange,
 * atlasSize) is wired into the shader so the rendered glyph quality
 * follows the source atlas exactly. The accentColor is not yet used
 * by the shader but is stored on userData for the PR-G follow-up.
 */

import * as THREE from "three";

import type { SdfAtlas } from "./sdfAtlasLoader";
import {
  TEXT_FRAGMENT_SHADER,
  TEXT_SHADER_VERSION,
  TEXT_VERTEX_SHADER,
} from "./textShaders.glsl";

export interface TextMaterialUserData {
  /** Atlas font name — used by the renderer for batch grouping. */
  fontName: string;
  /** Accent color reserved for future selection-state plumbing. */
  accentColor: string | null;
  /** Stable shader version tag the auditor / tests can assert on. */
  shaderVersion: string;
}

/**
 * Build a fresh `THREE.ShaderMaterial` for the given atlas.
 *
 * One material per font is the expected lifetime — Scene3DStage caches
 * by atlas font name. Disposal is the caller's responsibility (mirrors
 * the LineMaterial / MeshBasicMaterial ownership pattern in
 * `Scene3DStage.tsx`).
 */
export function createTextMaterial(
  atlas: SdfAtlas,
  accentColor?: string,
): THREE.ShaderMaterial {
  const material = new THREE.ShaderMaterial({
    name: `sdf-text:${atlas.fontName}`,
    vertexShader: TEXT_VERTEX_SHADER,
    fragmentShader: TEXT_FRAGMENT_SHADER,
    uniforms: {
      atlas: { value: atlas.texture },
      distanceRange: { value: atlas.distanceRange },
      atlasSize: { value: atlas.atlasSize },
    },
    transparent: true,
    depthWrite: false,
    depthTest: true,
    side: THREE.DoubleSide,
    blending: THREE.NormalBlending,
  });

  const userData: TextMaterialUserData = {
    fontName: atlas.fontName,
    accentColor: accentColor ?? null,
    shaderVersion: TEXT_SHADER_VERSION,
  };
  material.userData = userData;

  return material;
}
